import React from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  ButtonGroup,
} from '@chakra-ui/react';
import { Copy, FileText, FileX } from 'lucide-react';
import { ShareActions } from './ShareActions';
import { useShareActions } from '../hooks/useShareActions';

interface ShareTabProps {
  listName: string;
  shareUrl: string;
  listType: 'personal' | 'collaborative';
  // Personal list specific
  shareWithNotes?: boolean;
  onNotesToggle?: (withNotes: boolean) => void;
  isLoading?: boolean;
}

/**
 * ShareTab - Share link tab content for both personal and collaborative lists
 */
export function ShareTab({
  listName,
  shareUrl,
  listType,
  shareWithNotes,
  onNotesToggle,
  isLoading,
}: ShareTabProps) {
  const shareText = listType === 'collaborative'
    ? `Check out "${listName}" on Paire! Join us to vote on the best restaurants.`
    : `Check out "${listName}" on Paire!`;

  const {
    handleNativeShare,
    handleCopyLink,
  } = useShareActions({ shareUrl, shareText, listName });

  return (
    <VStack spacing={4} align="stretch">
      {/* List name preview */}
      <Box
        p={4}
        bg="purple.50"
        borderRadius="var(--r-inner)"
        border="1px solid"
        borderColor="purple.200"
      >
        <Text fontWeight="medium" color="purple.800" fontSize="sm">
          {listName}
        </Text>
      </Box>

      {/* Notes toggle for personal lists */}
      {listType === 'personal' && onNotesToggle && (
        <Box>
          <Text fontSize="xs" color="var(--card-text-3)" mb={2} fontWeight="medium">
            Share Options
          </Text>
          <ButtonGroup isAttached w="100%" size="md">
            <Button
              flex={1}
              variant={shareWithNotes ? 'solid' : 'outline'}
              bg={shareWithNotes ? 'purple.500' : undefined}
              color={shareWithNotes ? 'white' : 'var(--card-text-2)'}
              borderColor="var(--card-divider)"
              _hover={shareWithNotes ? { bg: 'purple.600' } : { bg: 'purple.50' }}
              leftIcon={<FileText size={16} />}
              onClick={() => onNotesToggle(true)}
              isDisabled={isLoading}
            >
              With Notes
            </Button>
            <Button
              flex={1}
              variant={!shareWithNotes ? 'solid' : 'outline'}
              bg={!shareWithNotes ? 'purple.500' : undefined}
              color={!shareWithNotes ? 'white' : 'var(--card-text-2)'}
              borderColor="var(--card-divider)"
              _hover={!shareWithNotes ? { bg: 'purple.600' } : { bg: 'purple.50' }}
              leftIcon={<FileX size={16} />}
              onClick={() => onNotesToggle(false)}
              isDisabled={isLoading}
            >
              Without Notes
            </Button>
          </ButtonGroup>
          {shareWithNotes && (
            <Text fontSize="xs" color="var(--card-text-3)" mt={2}>
              All notes on restaurants will be visible to anyone with the link.
            </Text>
          )}
        </Box>
      )}

      {/* Share URL with copy button */}
      <Box>
        <Text fontSize="xs" color="var(--card-text-3)" mb={2} fontWeight="medium">
          Share Link
        </Text>
        <InputGroup>
          <Input
            value={shareUrl || 'Generating link...'}
            isReadOnly
            bg="var(--card-surface-2)"
            fontSize="md"
            pr="4rem"
            color={shareUrl ? 'var(--card-text)' : 'var(--card-text-3)'}
            fontStyle={shareUrl ? 'normal' : 'italic'}
          />
          <InputRightElement width="4rem">
            <IconButton
              aria-label="Copy link"
              icon={<Copy size={16} />}
              size="sm"
              variant="ghost"
              color="purple.500"
              _hover={{ bg: 'purple.50' }}
              onClick={handleCopyLink}
              isDisabled={isLoading || !shareUrl}
            />
          </InputRightElement>
        </InputGroup>
      </Box>

      {/* Share button */}
      <Box pt={2}>
        <ShareActions
          onShare={handleNativeShare}
          isLoading={isLoading || !shareUrl}
        />
      </Box>

      {/* Info text */}
      <Text fontSize="xs" color="var(--card-text-3)" textAlign="center">
        {listType === 'collaborative'
          ? 'Anyone with this link can view the list. Only invited members can vote.'
          : 'Anyone with this link can view your shared list.'}
      </Text>
    </VStack>
  );
}
